import type { Surface } from '@funkit/connect-core'
import { useEffect, useRef } from 'react'

import { FiatStatusScreen } from '../FiatStatusScreen'
import { logger } from '../../logging/logger'
import { useFunkitTranslation } from '../../providers/FunkitI18nProvider'
import type { SurfaceAdapter, SurfaceContext } from './adapter'
import {
  stripePaymentErrorCode,
  stripePaymentErrorDetail,
} from './stripePayment'
import type { StripeOnrampClient } from './stripeOnramp'

interface StripeCheckoutParams {
  action: 'PERFORM_CHECKOUT'
  sessionId: string
  callbackEndpoint: string
  callbackIdField: string
}

/** Fetches one `client_secret` for a single Stripe checkout attempt. */
export type CheckoutClientSecretRequest = (params: {
  callbackEndpoint: string
  callbackIdField: string
}) => Promise<string>

export function matchStripeCheckoutParams(
  surface: Surface,
): StripeCheckoutParams | undefined {
  if (surface.kind !== 'PAY_SHEET' || surface.provider !== 'STRIPE') {
    return undefined
  }
  const params: unknown = surface.params
  if (params === null || typeof params !== 'object') {
    return undefined
  }
  const record = params as Record<string, unknown>
  if (
    record.action !== 'PERFORM_CHECKOUT' ||
    typeof record.sessionId !== 'string' ||
    record.sessionId.length === 0 ||
    typeof record.callbackEndpoint !== 'string' ||
    record.callbackEndpoint.length === 0 ||
    typeof record.callbackIdField !== 'string' ||
    record.callbackIdField.length === 0
  ) {
    return undefined
  }
  return {
    action: 'PERFORM_CHECKOUT',
    sessionId: record.sessionId,
    callbackEndpoint: record.callbackEndpoint,
    callbackIdField: record.callbackIdField,
  }
}

/**
 * Stripe's checkout loop, which is the only thing that finalises a purchase.
 *
 * The charge is not settled by our backend's checkout endpoint returning a
 * client secret — Stripe treats the transaction as final only once
 * `performCheckout` resolves, because it owns the next actions (3DS) between
 * attempts. So the callback endpoint is never called outside this callback,
 * and the surface stays mounted for the whole loop rather than reporting on
 * the first secret it receives.
 */
export function stripeCheckoutAdapter(
  stripe: StripeOnrampClient,
  requestClientSecret: CheckoutClientSecretRequest,
): SurfaceAdapter {
  return (surface) => {
    const params = matchStripeCheckoutParams(surface)
    if (params === undefined) {
      return undefined
    }
    return {
      control: 'HANDOFF',
      render: (context) => (
        <StripeCheckoutSurface
          stripe={stripe}
          params={params}
          requestClientSecret={requestClientSecret}
          context={context}
        />
      ),
    }
  }
}

function StripeCheckoutSurface({
  stripe,
  params,
  requestClientSecret,
  context,
}: {
  stripe: StripeOnrampClient
  params: StripeCheckoutParams
  requestClientSecret: CheckoutClientSecretRequest
  context: SurfaceContext
}) {
  const { t } = useFunkitTranslation()
  const mounted = useRef(true)
  const started = useRef(false)
  const contextRef = useRef(context)
  contextRef.current = context

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  useEffect(() => {
    if (started.current) {
      return
    }
    started.current = true
    void performStripeCheckout(stripe, params, requestClientSecret)
      .then(() => {
        if (!mounted.current) {
          logger.warn('connect-rn:fiat:stripe_checkout_unreported', {
            outcome: 'completed',
          })
          return
        }
        contextRef.current.report({ checkoutOutcome: 'completed' })
      })
      .catch((error: unknown) => {
        const code = stripePaymentErrorCode(error)
        logger.warn('connect-rn:fiat:stripe_checkout_failed', {
          code,
          ...stripePaymentErrorDetail(error),
        })
        if (!mounted.current) {
          logger.warn('connect-rn:fiat:stripe_checkout_unreported', {
            outcome: 'failed',
            code,
          })
          return
        }
        contextRef.current.onFailed(code)
      })
  }, [params, requestClientSecret, stripe])

  return (
    <FiatStatusScreen
      tone="waiting"
      title={t('fiat.processing')}
      // PAYMENT_CAPTURE + HANDOFF takes the dismiss lock, so without this a 3DS
      // sheet that never returns leaves the buyer with no way off the screen.
      onClose={() => contextRef.current.onClose()}
    />
  )
}

async function performStripeCheckout(
  stripe: StripeOnrampClient,
  params: StripeCheckoutParams,
  requestClientSecret: CheckoutClientSecretRequest,
): Promise<void> {
  logger.info('connect-rn:fiat:stripe_payment_stage_started', {
    stage: 'perform_checkout',
  })
  let attempts = 0
  const result = await stripe.performCheckout(params.sessionId, async () => {
    attempts += 1
    logger.info('connect-rn:fiat:stripe_checkout_callback', {
      attempt: attempts,
    })
    try {
      return await requestClientSecret({
        callbackEndpoint: params.callbackEndpoint,
        callbackIdField: params.callbackIdField,
      })
    } catch (error: unknown) {
      // Returning null is how the SDK is told this attempt cannot proceed; a
      // throw here crosses the native bridge as an opaque failure instead.
      logger.warn('connect-rn:fiat:stripe_checkout_callback_failed', {
        attempt: attempts,
        cause: error instanceof Error ? error.message : 'unknown',
      })
      return null
    }
  })
  if (result?.error !== undefined) {
    const failure = new Error(
      `perform_checkout:${stripePaymentErrorCode(result.error)}`,
    )
    throw Object.assign(failure, { cause: result.error })
  }
  logger.info('connect-rn:fiat:stripe_payment_stage_completed', {
    stage: 'perform_checkout',
    attempts,
  })
}
